import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Shield, Cpu, Radio, Search, ArrowUpRight } from "lucide-react";
import { SERVICES } from "@/data/services";
import { SectionKicker, SectionHeading, FadeIn } from "@/components/shared/SectionBits";

const ICONS = { shield: Shield, cpu: Cpu, radio: Radio, search: Search };
const HERO = "https://customer-assets-jai6qajn.emergentagent.net/job_dynamic-protect-dev/artifacts/dyjrjaon_ChatGPT%20Image%20Jul%2021%2C%202026%20at%2010_23_43%20AM.png";

export default function ServicesIndexPage() {
    return (
        <div data-testid="services-index-page">
            <section className="relative min-h-[55vh] flex items-end overflow-hidden">
                <div className="absolute inset-0">
                    <img src={HERO} alt="Abstract digital data visualization" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-r from-dr-navy/95 via-dr-navy/85 to-dr-navy/40" />
                </div>
                <div className="relative dr-container w-full py-24">
                    <SectionKicker>Services</SectionKicker>
                    <motion.h1
                        initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
                        className="font-display font-bold text-white text-5xl md:text-6xl lg:text-7xl leading-[0.98] max-w-4xl"
                    >
                        Specialist and bespoke consultancy in crisis and risk management.
                    </motion.h1>
                    <p className="text-white/80 text-lg md:text-xl mt-7 max-w-2xl">
                        Four practices, one integrated view of risk, delivered by senior consultants and calibrated
                        to your organisation.
                    </p>
                </div>
            </section>

            <section className="dr-container py-28 md:py-36">
                <div className="space-y-14">
                    {SERVICES.map((s, i) => {
                        const Icon = ICONS[s.icon] || Shield;
                        const flip = i % 2 === 1;
                        return (
                            <FadeIn key={s.slug} delay={i * 0.08}>
                                <div className="grid lg:grid-cols-12 gap-10 items-center">
                                    <div className={`lg:col-span-6 ${flip ? "lg:order-2" : ""}`}>
                                        <div className="relative aspect-[4/3] rounded-3xl overflow-hidden">
                                            <img src={s.image} alt={s.title} className="w-full h-full object-cover" />
                                            <div className="absolute inset-0 bg-gradient-to-t from-dr-navy/30 to-transparent" />
                                            <div className="absolute top-6 left-6 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/95 text-xs font-medium tracking-widest uppercase text-dr-navy">
                                                0{i + 1} · {s.heroTag.split(" · ")[0]}
                                            </div>
                                        </div>
                                    </div>
                                    <div className={`lg:col-span-6 ${flip ? "lg:order-1" : ""}`}>
                                        <span className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-dr-navy text-white">
                                            <Icon className="w-5 h-5" />
                                        </span>
                                        <h2 className="font-display font-bold text-dr-navy text-4xl md:text-5xl leading-tight mt-6">
                                            {s.title}
                                        </h2>
                                        <p className="text-dr-dark/75 text-lg mt-5 leading-relaxed">{s.summary}</p>
                                        <div className="flex flex-wrap gap-2 mt-6">
                                            {(s.subServices || s.items).slice(0, 4).map((it) => (
                                                <span key={it.title} className="text-xs px-3 py-1.5 rounded-full border border-dr-silver text-dr-navy/80 bg-dr-grey">
                                                    {it.title}
                                                </span>
                                            ))}
                                        </div>
                                        <Link
                                            to={`/services/${s.slug}`}
                                            data-testid={`services-index-cta-${s.slug}`}
                                            className="inline-flex items-center gap-2 mt-8 px-6 py-3 rounded-full bg-dr-navy text-white font-medium hover:bg-dr-blue transition-colors"
                                        >
                                            Explore {s.title}
                                            <ArrowUpRight className="w-4 h-4" />
                                        </Link>
                                    </div>
                                </div>
                            </FadeIn>
                        );
                    })}
                </div>
            </section>
        </div>
    );
}
